import React, { useState } from 'react';
import Modal from '../../../ui/Modal';
import Button from '../../../ui/Button';
import { XMarkIcon } from '@heroicons/react/24/outline';

const RejectAllReturnsModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isLoading = false,
  entity 
}) => {
  const [rejectionReason, setRejectionReason] = useState('');

  const orderItems = entity?.orderItems || [];
  const returnRequestedItems = orderItems.filter(item => 
    item.status === 'return_requested_by_customer'
  );

  const handleConfirm = () => {
    onConfirm({ rejectionReason });
  };

  const handleClose = () => {
    setRejectionReason('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Reject Return"
      size="lg"
    >
      <div className="py-4">
        <div className="flex items-center mb-4">
          <XMarkIcon className="h-12 w-12 text-red-500 mr-4" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              Reject All Return Requests
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              This will reject all pending return requests in this fulfillment group.
            </p>
          </div>
        </div>

        {returnRequestedItems.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No return requests found to reject.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Items to be rejected */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Items to be rejected ({returnRequestedItems.length}):
              </h4>
              
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 max-h-60 overflow-y-auto">
                {returnRequestedItems.map((item) => (
                  <div key={item.id} className="p-3">
                    <div className="flex items-center space-x-3">
                      {item.product?.images?.[0] && (
                        <img
                          src={item.product.images[0].url}
                          alt={item.product.name}
                          className="h-12 w-12 object-cover rounded-md"
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {item.product?.name || 'Product'}
                        </p>
                        <p className="text-sm text-gray-500">
                          Size: {item.size} • Quantity: {item.quantity}
                        </p>
                        <p className="text-sm font-medium text-gray-900">
                          ${item.price}
                        </p>
                        {item.returnRequest?.reason && (
                          <p className="text-sm text-gray-600 mt-1">
                            Customer reason: {item.returnRequest.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Rejection Reason */}
            <div>
              <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for rejection *
              </label>
              <select
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent"
                required
              >
                <option value="">Select a reason</option>
                <option value="outside_return_window">Outside return window</option>
                <option value="item_not_returnable">Item not returnable</option>
                <option value="insufficient_reason">Insufficient reason provided</option>
                <option value="item_used_damaged">Item appears used or damaged</option>
                <option value="policy_violation">Return policy violation</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
        )}

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 mt-4">
          <h4 className="text-sm font-medium text-red-900 mb-2">
            What happens next:
          </h4>
          <ul className="text-sm text-red-800 space-y-1">
            <li>• All return requests will be rejected</li>
            <li>• Customers will be notified with the rejection reason</li>
            <li>• Items will remain as delivered/completed</li>
            <li>• This action cannot be undone</li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row-reverse gap-3">
          <Button
            onClick={handleConfirm}
            disabled={isLoading || returnRequestedItems.length === 0 || !rejectionReason}
            isLoading={isLoading}
            variant="danger"
            className="flex items-center justify-center"
          >
            <XMarkIcon className="h-4 w-4 mr-2" />
            Reject Return ({returnRequestedItems.length})
          </Button>
          <Button
            onClick={handleClose}
            disabled={isLoading}
            variant="secondary"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default RejectAllReturnsModal; 